import { Check, ChevronDown, MoreHorizontal } from 'lucide-react'
import type { ComponentType } from 'react'
import { useCallback, useLayoutEffect } from 'react'
import './menu.css'
import { createPortal } from 'react-dom'
import { useAnchoredPopover } from './useAnchoredPopover'

type IconType = ComponentType<{ size?: number; 'aria-hidden'?: boolean }>

export type MenuItem = {
  label: string
  /**
   * Where the item goes. An item that NAVIGATES is an anchor, not a button:
   * middle-click, open-in-new-tab and the status bar all stop working the
   * moment a link is redrawn as an onClick, and on a public site those are the
   * gestures people actually use. Exactly one of `href`/`onSelect` is given.
   */
  href?: string
  onSelect?: () => void
  icon?: IconType
  /** Marks the item you are currently on — a tick, in the one color that says
   *  "your position". Not a hover or a fill; see the design system on accent. */
  selected?: boolean
  /** Marks an item that destroys something. It reads in the danger color and
   *  sits below a rule, so it is never the item under the cursor by default. */
  tone?: 'danger'
  /**
   * Marks an item with something behind it, as a mark rather than a number.
   *
   * For what a count cannot honestly say: a drawer that holds things nobody is
   * being asked to answer for. It stops a person opening an empty one without
   * claiming they owe it anything — so it is quiet, and it carries no figure.
   */
  holding?: boolean
}

/**
 * An overflow menu — a list of ACTIONS, which is what makes it a different thing
 * from a Select. A select shows a current value and changes it; this offers
 * commands, has no value, and closes on the one you pick.
 *
 * It exists so that rarely-used and destructive actions have somewhere to live
 * that isn't the surface people read. Delete in the open is a target; delete
 * behind a menu is a decision.
 */
export function Menu({
  items,
  ariaLabel,
  label,
  align = 'end',
  icon,
  tool = false,
}: {
  items: MenuItem[]
  ariaLabel: string
  /**
   * Renders the trigger as TEXT with a chevron instead of an icon button — for
   * the case where the thing you are on is also the way to change it (a
   * congregation's name in its own masthead). Without it the same job needs a
   * second popover written by hand, which is how a UI ends up with four menus
   * that dismiss differently.
   */
  label?: string
  /** Which edge the popover hangs from. `end` suits a trailing overflow button;
   *  a labeled trigger reads left-to-right, so it opens from its start edge. */
  align?: 'start' | 'end'
  icon?: IconType
  /**
   * A labeled trigger sitting among a band's tools rather than in a masthead:
   * it takes a quiet button's height and type, so the band reads as one row.
   */
  tool?: boolean
}) {
  const Icon = icon ?? MoreHorizontal
  // Shared with OverflowGroup, so the two popovers cannot come to dismiss
  // differently — one dismissal for every floating thing in the app.
  const { open, setOpen, ref, panelRef, triggerRef } =
    useAnchoredPopover<HTMLButtonElement>({ align })
  const focusTrigger = useCallback(
    () => triggerRef.current?.focus(),
    [triggerRef],
  )

  /* Drawn at the end of the document, the panel is no longer the next stop
     after its trigger, so opening it moves focus into it, and closing it
     while focus is still in it — Escape, or an item chosen — hands focus back
     to the trigger it came from. */
  useLayoutEffect(() => {
    if (!open) return
    const panel = panelRef.current
    panel?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    return () => {
      const active = document.activeElement
      if (active === document.body || (active && panel?.contains(active))) {
        focusTrigger()
      }
    }
  }, [focusTrigger, open, panelRef])

  return (
    <div className="menu" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={`menu__trigger${label ? ' menu__trigger--label' : ''}${tool ? ' menu__trigger--tool' : ''}`}
        data-open={open ? '' : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        /* A visible label IS the name, so it is not restated — unless the
           caller's name says something the word does not, which is how a
           state carried as a mark reaches a reader who cannot see it. It
           still opens with the visible word, so the two agree. */
        aria-label={label && ariaLabel === label ? undefined : ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        {label ? (
          /* A word and the mark that says it opens — never a glyph as well.
             The word is what identifies the trigger, so an icon beside it is a
             second answer to a question already answered, and the chevron is
             the one mark doing work the word cannot. Unlabeled, the glyph IS
             the identity and takes no chevron, for the same reason. */
          <>
            <span className="menu__trigger-text">{label}</span>
            <ChevronDown size={15} aria-hidden />
          </>
        ) : (
          <Icon size={16} aria-hidden />
        )}
      </button>
      {open
        ? createPortal(
            /* A portal leaves the DOM but not React's tree, so a pick still
               bubbles to a row that opens; `data-no-open` stops it there. */
            <div
              ref={panelRef}
              className="menu__pop"
              role="menu"
              aria-label={ariaLabel}
              data-no-open
            >
              {items.map((item) => {
                const ItemIcon = item.icon
                const className = `menu__item${item.tone === 'danger' ? ' menu__item--danger' : ''}`
                const body = (
                  <>
                    {ItemIcon ? <ItemIcon size={14} aria-hidden /> : null}
                    <span className="menu__item-label">{item.label}</span>
                    {item.holding ? (
                      <span className="menu__holding" aria-hidden />
                    ) : null}
                    {item.selected ? (
                      <Check className="menu__tick" size={14} aria-hidden />
                    ) : null}
                  </>
                )
                /* The mark is drawn for the eye alone, so the name is what says it
               to a reader who cannot see it. */
                const name = item.holding
                  ? `${item.label}, not empty`
                  : undefined
                return item.href ? (
                  <a
                    key={item.label}
                    role="menuitem"
                    className={className}
                    aria-label={name}
                    href={item.href}
                  >
                    {body}
                  </a>
                ) : (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    className={className}
                    aria-label={name}
                    onClick={() => {
                      setOpen(false)
                      item.onSelect?.()
                    }}
                  >
                    {body}
                  </button>
                )
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
